import { useCallback, useEffect } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm, useWatch } from 'react-hook-form'

import { Group, List, Message } from '~/components/component-library'
import { TokenBalance } from '~/components/component-library/TokenBalance'
import { TokenDisplay } from '~/components/component-library/TokenDisplay'
import { TransactionDialogContent } from '~/components/smart-accounts/transactions/TransactionDialogContent'
import { createTransactionHandlers } from '~/components/smart-accounts/transactions/createTransactionHandlers'
import { getProtocolLabel } from '~/components/smart-accounts/vesting/VestingBalances'
import {
  ClaimFormValues,
  claimSchema,
} from '~/components/smart-accounts/vesting/claimSchema'
import { useCallsToClaim } from '~/components/smart-accounts/vesting/hooks/useCallsToClaim'
import { useCreateClaimTransaction } from '~/components/smart-accounts/vesting/hooks/useCreateClaimTransaction'
import { useIsClaimFeeSufficient } from '~/components/smart-accounts/vesting/hooks/useIsClaimFeeSufficient'
import { VestingBalance } from '~/components/smart-accounts/vesting/types'
import {
  buildVestingClaimData,
  buildVestingClaimName,
} from '~/components/smart-accounts/vesting/utils'
import { QUERY_KEYS } from '~/constants/react-query'
import { useRequireMemo } from '~/hooks/org'
import { useActiveOrgId } from '~/hooks/org/useActiveOrgId'
import { useApproveAndSubmitTransaction } from '~/hooks/smart-account-actions/useApproveAndSubmitTransaction'
import {
  buildAddToBatchDropdownOptions,
  useAddCurrentFormToBatch,
} from '~/hooks/useAddCurrentFormToBatch'
import { useOrgSmartAccount } from '~/hooks/useSmartAccounts'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { useCanAddToBatch } from '~/hooks/useTransactionBatch'
import {
  BackendLoggingData,
  BasicTransactionType,
  MetadataType,
  OrgTransaction,
} from '~/types'

export const ClaimDialog = ({
  vestingBalance,
  setIsOpen,
}: {
  vestingBalance: VestingBalance
  setIsOpen: (isOpen: boolean) => void
}) => {
  const tokenMetadata = useTokenMetadata({
    address: vestingBalance.tokenAddress,
    chainId: vestingBalance.chainId,
    includePrice: true,
  })

  const claimFeeTokenMetadata = useTokenMetadata({
    address: vestingBalance.claimFee?.tokenAddress,
    chainId: vestingBalance.chainId,
  })

  const isClaimFeeSufficient = useIsClaimFeeSufficient(vestingBalance)

  const { data: callsToClaim, isLoading: isLoadingCallsToClaim } =
    useCallsToClaim(vestingBalance)

  const methods = useForm<ClaimFormValues>({
    defaultValues: {
      account: vestingBalance.accountAddress,
      chainId: vestingBalance.chainId,
      calls: callsToClaim ?? [],
      memo: `Claim from ${getProtocolLabel(vestingBalance.protocol)} stream`,
    },
    mode: 'onChange',
  })

  const { smartAccount: account } = useOrgSmartAccount(
    vestingBalance.accountAddress,
  )

  useEffect(() => {
    if (callsToClaim) {
      methods.setValue('calls', callsToClaim)
    }
  }, [callsToClaim, methods])

  const {
    approveAndSubmitTransaction,
    isProcessing: isProcessingApproveAndSubmit,
  } = useApproveAndSubmitTransaction()

  const activeOrgId = useActiveOrgId()

  const { createTransactionOnSubmit, isProcessingTransaction } =
    useCreateClaimTransactionSubmit({
      activeOrgId: activeOrgId,
      setIsOpen: setIsOpen,
      vestingBalance: vestingBalance,
    })

  const transactionHandlers = createTransactionHandlers<ClaimFormValues>({
    methods,
    senderAccount: account!,
    getLoggingData: () => ({
      vestingContract: vestingBalance.contractAddress,
      vestingToken: vestingBalance.tokenAddress,
      vestingAmount: vestingBalance.claimableAmount.toString(),
      vestingChainId: vestingBalance.chainId,
      vestingAccount: vestingBalance.accountAddress,
      vestingAccountId: vestingBalance.smartAccountId,
    }),
    createTransactionOnSubmit,
    approveAndSubmitTransaction,
  })

  const memo = useWatch({ control: methods.control, name: 'memo' })
  const requireMemo = useRequireMemo()

  const isValidForm =
    claimSchema.safeParse(methods.getValues()).success &&
    isClaimFeeSufficient &&
    !(requireMemo && !memo?.trim())

  const batchTitle = buildVestingClaimName({ vestingBalance, tokenMetadata })
  const canAddToBatch = useCanAddToBatch({
    smartAccountId: account?.id,
    chainId: vestingBalance.chainId,
  })
  const { addCurrentFormToBatch, isProcessing: isAddingToBatch } =
    useAddCurrentFormToBatch()

  const handleAddToBatch = async () => {
    if (!account || !callsToClaim?.length) return
    await addCurrentFormToBatch({
      smartAccountId: account.id,
      chainId: vestingBalance.chainId,
      title: batchTitle,
      types: [BasicTransactionType.Claim],
      calls: callsToClaim,
      metadata: [
        {
          type: MetadataType.Claim,
          ...buildVestingClaimData({
            vestingBalance,
            chainId: vestingBalance.chainId,
            tokenMetadata,
          }),
        },
      ],
      memo: methods.getValues('memo'),
      setIsOpen,
    })
  }

  return (
    <FormProvider {...methods}>
      <TransactionDialogContent
        id="claimVestingBalance"
        title={batchTitle}
        isLoading={
          isProcessingTransaction ||
          isProcessingApproveAndSubmit ||
          isLoadingCallsToClaim
        }
        isValidForm={isValidForm}
        handlers={transactionHandlers}
        smartAccountAddress={vestingBalance.accountAddress}
        chainId={vestingBalance.chainId}
        eventType="Claim"
        transactionTypes={[BasicTransactionType.Claim]}
        addressesToWatch={[vestingBalance.accountAddress]}
        reviewDropdownOptions={buildAddToBatchDropdownOptions({
          canAddToBatch,
          isDisabled: !isValidForm || isAddingToBatch,
          onClick: handleAddToBatch,
        })}
      >
        <div className="flex flex-col space-y-4">
          <List
            label="Claim amount"
            items={[
              {
                key: vestingBalance.tokenAddress,
                content: (
                  <TokenDisplay
                    address={vestingBalance.tokenAddress}
                    chainId={vestingBalance.chainId}
                    layout="two-line"
                    protocol={getProtocolLabel(vestingBalance.protocol)}
                  />
                ),
                meta: (
                  <TokenBalance
                    address={vestingBalance.tokenAddress}
                    chainId={vestingBalance.chainId}
                    tokenAmount={vestingBalance.claimableAmount}
                    layout="standard"
                    size="base"
                    direction="neutral"
                  />
                ),
              },
            ]}
          />
          {vestingBalance.claimFee && (
            <Group
              label="Claim fee"
              secondaryDisplay={
                <TokenBalance
                  address={vestingBalance.claimFee.tokenAddress}
                  chainId={vestingBalance.chainId}
                  tokenAmount={vestingBalance.claimFee.amount}
                  layout="inline"
                  size="base"
                  direction="neutral"
                />
              }
              tooltip={`${getProtocolLabel(vestingBalance.protocol)} charges a fee to claim vested tokens`}
            />
          )}
        </div>
        {!isClaimFeeSufficient && claimFeeTokenMetadata && (
          <Message
            variant="danger"
            title={`Not enough ${claimFeeTokenMetadata?.symbol}`}
          >
            <span>
              You do not have enough {claimFeeTokenMetadata?.symbol} to cover
              the claim fee
            </span>
          </Message>
        )}
      </TransactionDialogContent>
    </FormProvider>
  )
}

const useCreateClaimTransactionSubmit = ({
  activeOrgId,
  setIsOpen,
  vestingBalance,
}: {
  activeOrgId?: string
  setIsOpen: (isOpen: boolean) => void
  vestingBalance: VestingBalance
}) => {
  const queryClient = useQueryClient()
  const { createClaimTransaction, isProcessing: isProcessingTransaction } =
    useCreateClaimTransaction()

  const tokenMetadata = useTokenMetadata({
    address: vestingBalance.tokenAddress,
    chainId: vestingBalance.chainId,
  })

  const createTransactionOnSubmit = useCallback(
    (
      dontOpenDialog: boolean,
      callback?: (transaction: OrgTransaction) => void,
    ) =>
      async ({
        data,
        loggingData,
      }: {
        data: ClaimFormValues
        loggingData?: BackendLoggingData
      }) => {
        await createClaimTransaction(
          {
            claimForm: data,
            tokenMetadata: tokenMetadata,
            vestingBalance: vestingBalance,
            loggingData: loggingData,
          },
          dontOpenDialog,
          (transaction) => {
            queryClient.setQueryData(
              [QUERY_KEYS.TRANSACTION, activeOrgId, transaction.id],
              transaction,
            )
            setIsOpen(false)
            callback?.(transaction)
          },
        )
      },

    [
      tokenMetadata,
      vestingBalance,
      createClaimTransaction,
      queryClient,
      activeOrgId,
      setIsOpen,
    ],
  )

  return {
    createTransactionOnSubmit,
    isProcessingTransaction,
  }
}
